import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Calendar, BookOpen, Users, Download, UserPlus, LogIn, Lock, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';
import usePSDirectoryLaunch from '../../hooks/usePSDirectoryLaunch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MatrimonialPage = () => {
  const [ebook2026, setEbook2026] = useState(null);
  const psDirectoryLaunched = usePSDirectoryLaunch();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/ps/ebook`).then(res => {
      if (res.data.available) setEbook2026(res.data);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="gradient-orange py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-4">
            <a href="/" className="hover:text-white">Home</a>
            <span>&rsaquo;</span>
            <a href="/families-register" className="hover:text-white">Families Data</a>
            <span>&rsaquo;</span>
            <span>Matrimonial</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Matrimonial</h1>
          <p className="text-2xl text-white/90 font-serif">वैवाहिकी - परिचय संगम</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-pink-50 to-transparent p-6 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                <Heart className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">परिचय संगम</h2>
                <p className="text-gray-500">Parichay Sangam - Matrimonial Meet</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-8">
            {/* Parichay Sangam 2026 — Post-event Announcement */}
            <div className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 border-2 border-orange-300 rounded-xl p-6" data-testid="ps-2026-announcement">
              <h3 className="text-xl font-bold text-orange-800 mb-1 flex items-center gap-2">
                <span aria-hidden="true">🌸</span> परिचय संगम 2026
              </h3>
              <p className="text-sm font-semibold text-orange-700 mb-4">महासभा का प्रकल्प</p>

              <div className="text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  <span aria-hidden="true">📖</span> परिचय संगम 2026 के पंजीकृत प्रत्याशियों की ऑनलाइन मैट्रीमोनियल डायरेक्टरी अब उपलब्ध है।
                </p>

                <div className="flex flex-wrap gap-3 pt-1">
                  {psDirectoryLaunched ? (
                    <Link to="/parichay-sangam/directory" data-testid="ps-directory-link">
                      <Button className="bg-pink-600 hover:bg-pink-700 text-white font-semibold">
                        <Users className="w-4 h-4 mr-2" /> <span className="mr-1" aria-hidden="true">📌</span> ऑनलाइन मैट्रीमोनियल डायरेक्टरी
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      disabled
                      title="Available from 13 June 2026, 9:00 AM IST"
                      className="bg-gray-400 text-white font-semibold cursor-not-allowed opacity-60"
                      data-testid="ps-directory-link"
                    >
                      <Users className="w-4 h-4 mr-2" /> <span className="mr-1" aria-hidden="true">📌</span> ऑनलाइन मैट्रीमोनियल डायरेक्टरी
                    </Button>
                  )}
                </div>

                <p className="bg-white/60 border border-orange-200 rounded-lg p-3">
                  <span aria-hidden="true">💡</span> ऑनलाइन डायरेक्टरी अधिक स्मार्ट, सुविधाजनक एवं इंटरैक्टिव माध्यम है।
                </p>

                <p>
                  <span aria-hidden="true">❤️</span> किसी प्रत्याशी में रुचि व्यक्त करने हेतु उसकी प्रोफ़ाइल खोलें और <strong>&ldquo;Express Interest&rdquo;</strong> बटन पर क्लिक करें। आपका संदेश संबंधित प्रत्याशी/परिवार को ई-मेल द्वारा प्रेषित हो जाएगा।
                </p>

                <p>
                  <span aria-hidden="true">🔐</span> डायरेक्टरी का उपयोग करने के लिए <strong>Parichay Sangam 2026 ID</strong> अथवा <strong>Family Registration ID</strong> से लॉगिन करें।
                </p>

                <div className="flex flex-wrap gap-3 pt-1">
                  <Link to="/parichay-sangam/login" data-testid="ps-login-btn">
                    <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50 font-semibold">
                      <LogIn className="w-4 h-4 mr-2" /> Login / लॉगइन
                    </Button>
                  </Link>
                </div>

                <p className="pt-2 text-center text-gray-700 font-medium">
                  सभी पंजीकृत परिवारों एवं प्रत्याशियों को शुभकामनाएँ।
                </p>
                <p className="text-center text-gray-600">
                  <span aria-hidden="true">🙏</span> टीम, परिचय संगम 2026
                </p>
              </div>
            </div>

            {/* Historical reference document */}
            <div className="border border-gray-200 rounded-xl p-5 bg-white" data-testid="ps-engagement-historical-doc">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-pink-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-pink-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-bold text-gray-900 mb-1">सगाई-संबंधों की ऐतिहासिक पृष्ठभूमि, चुनौतियाँ एवं समाधान</h4>
                  <p className="text-xs text-gray-500 mb-3">Engagement &amp; Marriage — Historical Background, Challenges &amp; Solutions</p>
                  <a
                    href="https://customer-assets.emergentagent.com/job_abcfafc4-09e8-4929-917e-c56a46d51339/artifacts/bq3gz1et_%E0%A4%B8%E0%A4%97%E0%A4%BE%E0%A4%88-%E0%A4%B8%E0%A4%82%E0%A4%AC%E0%A4%82%E0%A4%A7%E0%A5%8B%E0%A4%82%20%E0%A4%95%E0%A5%80%20%E0%A4%90%E0%A4%A4%E0%A4%BF%E0%A4%B9%E0%A4%BE%E0%A4%B8%E0%A4%BF%E0%A4%95%20%E0%A4%AA%E0%A5%83%E0%A4%B7%E0%A5%8D%E0%A4%A0%E0%A4%AD%E0%A5%82%E0%A4%AE%E0%A4%BF%2C%20%E0%A4%9A%E0%A5%81%E0%A4%A8%E0%A5%8C%E0%A4%A4%E0%A4%BF%E0%A4%AF%E0%A4%BE%E0%A4%81%20%E0%A4%8F%E0%A4%B5%E0%A4%82%20%E0%A4%B8%E0%A4%AE%E0%A4%BE%E0%A4%A7%E0%A4%BE%E0%A4%A8.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-3 py-1.5 rounded-md text-sm font-semibold transition-colors"
                    data-testid="ps-engagement-historical-pdf-link"
                  >
                    <Download className="w-3.5 h-3.5" /> Download PDF / पीडीएफ डाउनलोड करें
                  </a>
                </div>
              </div>
            </div>

            {/* आभार संदेश — Gratitude / Thank You message after the event */}
            <div className="bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 border-2 border-pink-300 rounded-xl p-6 shadow-sm" data-testid="ps-2026-gratitude">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-pink-200">
                <span className="text-2xl" aria-hidden="true">🙏</span>
                <h3 className="text-xl font-bold text-rose-800">आभार संदेश</h3>
              </div>
              <div className="text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  परिचय संगम 2026 <span className="text-gray-500">(महासभा का एक प्रकल्प)</span> का यह सुंदर, सफल एवं अविस्मरणीय आयोजन आप सभी के स्नेह, सहयोग एवं सहभागिता से संपन्न हुआ।
                </p>
                <p>
                  इस अवसर पर हम हृदय से उन सभी समाजबंधुओं का आभार व्यक्त करते हैं जिन्होंने प्रत्यक्ष अथवा अप्रत्यक्ष रूप से इस आयोजन को सफल बनाने में अपना अमूल्य योगदान दिया।
                </p>
                <p>
                  हम सभी <strong>संगम मित्रों</strong>, <strong>स्वयंसेवक भाई-बहनों</strong>, <strong>आयोजन समिति</strong>, <strong>समन्वयक श्री रतनलालजी कीरी</strong>, <strong>आईटी टीम</strong>, <strong>बैक ऑफिस टीम</strong> एवं प्रत्येक सहयोगी के प्रति विशेष कृतज्ञता व्यक्त करते हैं, जिन्होंने सेवा, समर्पण और टीम भावना के साथ इस आयोजन को सफलता के शिखर तक पहुँचाने में महत्वपूर्ण भूमिका निभाई।
                </p>
                <p>
                  साथ ही, सहयोगी संस्था के रूप में <strong>बालोतरा समाज संस्था</strong> का प्राप्त सहयोग एवं योगदान भी अत्यंत सराहनीय रहा, जिसके लिए हम उनके प्रति हार्दिक आभार प्रकट करते हैं।
                </p>
                <p>
                  हम सभी प्रतिभागी युवक-युवतियों, उनके अभिभावकों तथा कार्यक्रम में उपस्थित सभी समाजबंधुओं का भी विशेष धन्यवाद करते हैं, जिनकी सक्रिय सहभागिता ने इस संगम को सार्थकता एवं गरिमा प्रदान की।
                </p>
                <p className="bg-white/60 border-l-4 border-rose-400 pl-4 py-2 italic text-gray-700">
                  किसी भी बड़े आयोजन में सर्वोत्तम प्रयासों के बावजूद अनजाने में कुछ त्रुटियाँ अथवा व्यवस्थागत कमियाँ रह जाना स्वाभाविक है। यदि किसी भी समाजबंधु को किसी प्रकार की असुविधा हुई हो अथवा हमारी किसी व्यवस्था या व्यवहार से ठेस पहुँची हो, तो हम उसके लिए विनम्रतापूर्वक क्षमाप्रार्थी हैं।
                </p>
                <p>
                  आप सभी का विश्वास, स्नेह एवं सहयोग ही हमारी सबसे बड़ी शक्ति है। इसी विश्वास के साथ हम भविष्य में भी समाजहित के ऐसे प्रयासों को और अधिक सफल एवं प्रभावी बनाने का संकल्प लेते हैं।
                </p>
                <p className="text-center text-base font-semibold text-rose-700 pt-2">
                  🙏 आप सभी का हृदय से धन्यवाद, साधुवाद एवं अभिनंदन। 🙏
                </p>

                {/* Signatures */}
                <div className="grid grid-cols-2 gap-4 pt-5 mt-3 border-t border-pink-200 text-center">
                  <div>
                    <p className="font-bold text-gray-900">पुष्पकुमार भूत</p>
                    <p className="text-xs text-gray-600">महासचिव</p>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">जेठानंद कीरी</p>
                    <p className="text-xs text-gray-600">अध्यक्ष</p>
                  </div>
                </div>
                <p className="text-center text-xs text-gray-500 -mt-2">महासभा</p>
              </div>
            </div>

            {/* About Section */}
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 leading-relaxed">
                समाज में विवाह योग्य युवक-युवतियों का परिचय आदान-प्रदान कराना महासभा का एक उद्देश्य व कार्य है। इस उद्देश्य की पूर्ति हेतु <strong className="text-primary">परिचय संगम 2025</strong> का आयोजन महत्वपूर्ण, उपयोगी व उत्साहवर्द्धक सिद्ध हुआ। ऐसे आयोजन बार बार करते रहने की मांग आ रही है। तदनुसार परिचय संगम का आयोजन फिर किया जायेगा।
              </p>
            </div>

            {/* Download 2025 eBook Section */}
            <div className="bg-gradient-to-r from-pink-50 to-orange-50 border border-pink-200 rounded-xl p-6">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Download className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">परिचय संगम 2025 eBook</h3>
                  <p className="text-gray-600 text-sm mb-3">परिचय संगम 2025 की पूर्ण पुस्तिका डाउनलोड करें</p>
                  <a href="https://drive.google.com/file/d/1Z-r6RpL_RD3GeKY2s0COGYJiOG-ft17H/view?usp=sharing" target="_blank" rel="noopener noreferrer" data-testid="download-parichay-sangam-ebook">
                    <Button className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-semibold">
                      <Download className="w-4 h-4 mr-2" /> Download eBook 2025 (PDF)
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Parichay Sangam 2026 eBook — shown ONLY after admin publishes */}
            {ebook2026 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6" data-testid="ps-2026-ebook-section">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{ebook2026.title || 'परिचय संगम 2026 eBook'}</h3>
                    <p className="text-gray-600 text-sm mb-3">परिचय संगम 2026 की पूर्ण पुस्तिका डाउनलोड करें / Download the complete Parichay Sangam 2026 booklet</p>
                    <a href={ebook2026.url} target="_blank" rel="noopener noreferrer" data-testid="download-ps-2026-ebook">
                      <Button className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold">
                        <Download className="w-4 h-4 mr-2" /> Download eBook 2026 (PDF)
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Notice */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Privacy / गोपनीयता</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    सभी पंजीकृत प्रत्याशियों का डेटा पूर्णतः गोपनीय रखा जाता है। कोई भी व्यक्ति (पंजीकृत प्रत्याशी सहित) अन्य प्रत्याशियों का डेटा नहीं देख सकता। केवल प्रशासक ही सभी डेटा देख सकते हैं। कार्यक्रम के बाद सभी प्रत्याशियों की जानकारी एक eBook (PDF) में प्रकाशित की जाएगी।
                  </p>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                    All registered candidate data is kept strictly confidential. No user (including registered candidates) can view other candidates' data. Only admin has access. After the event, a consolidated eBook will be published.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default MatrimonialPage;
